import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useConversation } from '../../contexts/ConversationContext';
import { useSendTo, formatTranscript } from '../../hooks/useSendTo';
import { SendToModal } from './SendToModal';
import { SendDestination } from '../../types/sendTo';
import { FiSend, FiFileText, FiPackage, FiGitBranch } from 'react-icons/fi';

interface SendToMenuProps {
  sessionId: string;
  personaName: string;
  personaId: string;
}

export const SendToMenu: React.FC<SendToMenuProps> = ({
  sessionId,
  personaName,
  personaId
}) => {
  const { darkMode } = useTheme();
  const { state } = useConversation();
  const { summarize, isSummarizing, sendToDestination, isSending, error } = useSendTo();

  const [showDropdown, setShowDropdown] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [summaryContent, setSummaryContent] = useState('');
  const [lastDestination, setLastDestination] = useState<SendDestination>('artifactory_project');
  const menuRef = useRef<HTMLDivElement>(null);

  const chatMessages = state.messages.filter(m => m.role === 'user' || m.role === 'assistant');
  const hasMessages = chatMessages.length > 0;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSummarizeAndSend = async () => {
    setShowDropdown(false);
    setShowModal(true);
    setSummaryContent('');
    try {
      const summary = await summarize(sessionId);
      setSummaryContent(summary);
    } catch {
      // Error is handled in the hook; modal stays open for manual entry
    }
  };

  const handleSendFull = async (destination: SendDestination) => {
    setShowDropdown(false);
    const transcript = formatTranscript(state.messages, personaName, sessionId);
    try {
      await sendToDestination(transcript, destination, {
        sessionId,
        personaId,
        personaName,
        sendMode: 'full',
        messageCount: chatMessages.length,
        // Pass the full message array so the artifact save path can
        // build a structured PERSONA_CHAT artifact (each message
        // re-renders with its phase bubble intact on the viewer page).
        messages: state.messages,
      });
    } catch {
      // error is surfaced via useSendTo's error state
    }
  };

  const handleModalConfirm = async (content: string, destination: SendDestination) => {
    setLastDestination(destination);
    await sendToDestination(content, destination, {
      sessionId,
      personaId,
      personaName,
      sendMode: 'summarize',
      messageCount: chatMessages.length
    });
    setShowModal(false);
  };

  return (
    <>
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={!hasMessages}
          className={`px-3 py-1.5 rounded-md text-sm flex items-center transition-colors ${
            !hasMessages
              ? 'opacity-40 cursor-not-allowed'
              : darkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          }`}
          title={hasMessages ? 'Send conversation to...' : 'No messages to send'}
        >
          <FiSend className="w-3.5 h-3.5 mr-1.5" />
          Send To
        </button>

        {showDropdown && (
          <div className={`absolute right-0 bottom-full mb-1 w-56 rounded-md shadow-lg z-20 ${
            darkMode ? 'bg-gray-800 ring-1 ring-gray-700' : 'bg-white ring-1 ring-gray-200'
          }`}>
            <div className="py-1">
              {/* Summarize & Send */}
              <button
                onClick={handleSummarizeAndSend}
                className={`w-full px-4 py-2 text-sm text-left flex items-center ${
                  darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <FiFileText className="w-4 h-4 mr-2 text-blue-500" />
                Summarize &amp; Send...
              </button>

              {/* Divider */}
              <div className={`my-1 border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`} />

              {/* Send Full Chat sub-items */}
              <div className={`px-4 py-1 text-xs font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Send Full Chat to:
              </div>
              <button
                onClick={() => handleSendFull('artifactory_project')}
                className={`w-full px-4 py-2 text-sm text-left flex items-center ${
                  darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <FiPackage className="w-4 h-4 mr-2 text-purple-500" />
                New Artifactory Project
              </button>
              <button
                onClick={() => handleSendFull('artifact')}
                className={`w-full px-4 py-2 text-sm text-left flex items-center ${
                  darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <FiFileText className="w-4 h-4 mr-2 text-green-500" />
                New Artifact
              </button>
              <button
                disabled
                className={`w-full px-4 py-2 text-sm text-left flex items-center opacity-40 cursor-not-allowed ${
                  darkMode ? 'text-gray-500' : 'text-gray-400'
                }`}
                title="Coming soon"
              >
                <FiGitBranch className="w-4 h-4 mr-2 text-orange-500" />
                New Workflow
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Cooking overlay — shown while scaffolding/sending */}
      {isSending && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60">
          <div className={`flex flex-col items-center gap-4 px-10 py-8 rounded-2xl shadow-2xl ${
            darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
          }`}>
            <div className="text-6xl animate-bounce">🍳</div>
            <div className="text-lg font-semibold">Cooking your project...</div>
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              The AI is scaffolding your blueprint. This can take up to a minute.
            </div>
            <div className="flex space-x-2 mt-1">
              <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" />
              <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '0.2s' }} />
              <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '0.4s' }} />
            </div>
          </div>
        </div>
      )}

      <SendToModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        initialContent={summaryContent}
        isLoadingSummary={isSummarizing}
        onConfirm={handleModalConfirm}
        isSending={isSending}
        error={error}
        sessionId={sessionId}
        personaName={personaName}
        defaultDestination={lastDestination}
      />
    </>
  );
};
